import { useLocation, useNavigate } from "@tanstack/solid-router";
import { msg } from "@lingui/core/macro";
import { queryKeys } from "@bso/shared";
import { Menu } from "@fleet/icons";

import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import { GlobalSearch } from "@/features/search/components/global-search";
import { AppearanceMenu } from "@/components/appearance-menu";
import { useI18n } from "@/providers/i18n-provider";

import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { useSession } from "@/lib/auth";
import { createPowerSyncQuery } from "@bso/shared/solid";
import { bible_version } from "@bso/powersync";

interface AppHeaderProps {
  onMenuClick: () => void;
  isNavigating: () => boolean;
}

interface SavedBible {
  version: string;
  bookAbbr: string;
  chapter: string;
}

function isSavedBible(obj: unknown): obj is SavedBible {
  if (obj === null || typeof obj !== "object") {
    return false;
  }
  const version: unknown = Reflect.get(obj, "version");
  const bookAbbr: unknown = Reflect.get(obj, "bookAbbr");
  const chapter: unknown = Reflect.get(obj, "chapter");
  return typeof version === "string" && typeof bookAbbr === "string" && typeof chapter === "string";
}

export function AppHeader(props: AppHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();

  // Database contexts
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const session = useSession();
  const { userId } = session;

  // Query available Bible versions reactively
  const versionsQuery = createPowerSyncQuery({
    queryKey: () => queryKeys.bible.versions(userId()),
    db,
    queryFn: () => {
      const dbInstance = drizzle();
      if (!dbInstance) {
        return null;
      }
      return dbInstance.select().from(bible_version);
    },
    enabled: () => Boolean(drizzle()) && Boolean(db()),
  });

  const getLastRead = (): SavedBible => {
    try {
      const saved = localStorage.getItem("last-read-bible");
      if (saved) {
        const parsed: unknown = JSON.parse(saved);
        if (isSavedBible(parsed)) {
          return parsed;
        }
      }
    } catch {
      /* ignore storage or parsing errors */
    }
    return { version: "kjv", bookAbbr: "gen", chapter: "1" };
  };

  const activeVersion = (): string => {
    const match = /^\/bible\/([^/]+)/.exec(location().pathname);
    if (match?.[1]) {
      return match[1];
    }
    return getLastRead().version;
  };

  const availableVersions = () => {
    const { data } = versionsQuery;
    if (data && data.length > 0) {
      return data;
    }
    return [{ id: "kjv", name: "King James Version", abbr: "kjv" }];
  };

  const handleVersionChange = (newVersion: string) => {
    const match = /^\/bible\/([^/]+)\/([^/]+)\/([^/]+)/.exec(location().pathname);
    if (match) {
      const [_full, _version, bookAbbr, chapter] = match;
      if (bookAbbr && chapter) {
        void navigate({
          to: "/bible/$version/$bookAbbr/$chapter",
          params: {
            version: newVersion,
            bookAbbr,
            chapter,
          },
        });
      }
    } else {
      try {
        const saved = localStorage.getItem("last-read-bible");
        let bookAbbr = "gen";
        let chapter = "1";
        if (saved) {
          const parsed: unknown = JSON.parse(saved);
          if (isSavedBible(parsed)) {
            ({ bookAbbr } = parsed);
            ({ chapter } = parsed);
          }
        }
        localStorage.setItem(
          "last-read-bible",
          JSON.stringify({ version: newVersion, bookAbbr, chapter }),
        );
      } catch {
        /* ignore storage or parsing errors */
      }
    }
  };

  return (
    <header class="relative flex h-16 items-center justify-between border-b border-border/20 bg-card/5 px-4 backdrop-blur-sm md:px-6">
      {/* Subtle Loading Bar */}
      <div
        class="absolute bottom-[-1px] left-0 h-[2px] bg-accent transition-[width,opacity] duration-500 ease-out z-50"
        style={{
          width: props.isNavigating() ? "70%" : "0%",
          opacity: props.isNavigating() ? "1" : "0",
          "box-shadow": props.isNavigating() ? "0 0 8px var(--accent)" : "none",
        }}
      />
      <div class="flex items-center gap-4 flex-1">
        <Button
          variant="ghost"
          size="icon"
          class="h-9 w-9 lg:hidden text-muted-foreground hover:text-foreground"
          onClick={props.onMenuClick}
        >
          <Menu class="h-5 w-5" />
        </Button>
        <GlobalSearch />

        {/* Translation Selector Dropdown */}
        <Select
          value={activeVersion()}
          onChange={(value) => {
            handleVersionChange(value);
          }}
          aria-label={t(msg`Bible version`)}
          options={availableVersions().map((v) => {
            const value = v.abbr ?? v.id;
            const label = v.name ? `${v.abbr?.toUpperCase()} - ${v.name}` : v.id.toUpperCase();
            return { value, label, textValue: label };
          })}
        />
      </div>
      <div class="flex items-center gap-1">
        {/* Un solo control de apariencia: modo de color, desenfoque y galería.
            Antes eran tres botones para la misma familia de ajustes; los dos
            huecos que quedan son para utilidades de otro tipo. */}
        <AppearanceMenu />
      </div>
    </header>
  );
}
